/* eslint-disable max-len*/
import { Form, Formik } from 'formik';
import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StyledSingleDropdown from 'src/components/StyledSingleDropdown/StyledSingleDropdown';
import { appRoutePaths } from 'src/services/route.service';
import { LiteracyScreeningListPageValidationSchema } from 'src/types/Validation/LiteracyScreeningListPageValidationSchema';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import FormTextbox from '../../../../components/FormInputs/FormTextbox/FormTextbox';
import Modal from '../../../../components/Modal/Modal';
import Panel from '../../../../components/Panel/Panel';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectSelectedDistrict } from '../../../../redux/slices/appSlice';
import {
  initialContactFormValues,
  selectLiteracyScreeningContact,
  selectLiteracyScreeningLoadingContact,
  selectLiteracyScreeningLoadingSchoolYears,
  selectLiteracyScreeningSchoolYears,
} from '../../../../redux/slices/literacyScreeningSlice';
import { createNotification } from '../../../../redux/slices/notificationSlice';
import {
  createLiteracyScreeningContact,
  fetchLiteracyScreeningContact,
  fetchLiteracyScreeningSchoolYears,
  updateLiteracyScreeningContact,
} from '../../../../redux/thunks/literacyScreeningThunks';
import { ButtonSize } from '../../../../types/ButtonSize';
import { DialogType } from '../../../../types/DialogTypes';
import { FeatureAccessPermissions } from '../../../../types/FeatureAccessPermissions';
import { LiteracyScreeningContactModel } from '../../../../types/LiteracyScreeningContactModel';
import { PanelType } from '../../../../types/PanelType';
import {
  hasSpecifiedFeatureAccess,
  isStateAdmin,
  isStateHelpdesk,
} from '../../../../utilities/userUtilities';
import DistrictSchoolInfoPage from '../DistrictSchoolInfoPage';
import './LiteracyScreeningList.css';

const LiteracyScreeningList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const contact = useAppSelector(selectLiteracyScreeningContact);
  const selectedDistrict = useAppSelector(selectSelectedDistrict);
  const isLoadingContactList = useAppSelector(
    selectLiteracyScreeningLoadingContact
  );
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [resetFormFunction, setResetFormFunction] = useState<
    (() => void) | null
  >(null);
  const schoolYearOptions = useAppSelector(selectLiteracyScreeningSchoolYears);
  const isLoadingSchoolYears = useAppSelector(
    selectLiteracyScreeningLoadingSchoolYears
  );
  const [selectedSchoolYearOption, setSelectedSchoolYearOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const disableSchoolYearActions = isFormDirty || isSaving;
  const isContactAvailable = Boolean(contact?.id);
  const canEditContact =
    isStateAdmin() ||
    (!isStateHelpdesk() &&
      hasSpecifiedFeatureAccess(
        FeatureAccessPermissions.LITERACY_SCREENING_ACCESS
      ));
  const isContactReadOnly = !canEditContact;
  const disableEditView =
    !isContactAvailable ||
    disableSchoolYearActions ||
    !selectedSchoolYearOption?.value;

  useEffect(() => {
    dispatch(fetchLiteracyScreeningContact({ showLoading: true }));
    dispatch(fetchLiteracyScreeningSchoolYears());
  }, [dispatch]);

  useEffect(() => {
    if (schoolYearOptions.length > 0 && !selectedSchoolYearOption) {
      const mostRecentYear = schoolYearOptions[0];
      setSelectedSchoolYearOption({
        value: mostRecentYear.value,
        label: mostRecentYear.text,
      });
    }
  }, [schoolYearOptions, selectedSchoolYearOption]);

  useEffect(() => {
    if (!isLoadingContactList && !isLoadingSchoolYears && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoadingContactList, isLoadingSchoolYears, isInitialLoad]);

  const getInitialValues = (): LiteracyScreeningContactModel => {
    if (contact) {
      return {
        id: contact.id || '',
        leaIdentifier:
          contact.leaIdentifier || selectedDistrict.districtId || '',
        firstName: contact.firstName || '',
        lastOrSurname: contact.lastOrSurname || '',
        position: contact.position || '',
        email: contact.email || '',
        telephone: contact.telephone || '',
      };
    }
    return {
      ...initialContactFormValues,
      leaIdentifier: selectedDistrict.districtId || '',
    };
  };

  const handleSubmit = async (
    values: LiteracyScreeningContactModel,
    resetForm: () => void
  ): Promise<void> => {
    setIsSaving(true);
    try {
      const contactData: LiteracyScreeningContactModel = {
        id: values?.id || '',
        leaIdentifier:
          values?.leaIdentifier || selectedDistrict.districtId || '',
        firstName: values?.firstName || '',
        lastOrSurname: values?.lastOrSurname || '',
        position: values?.position || '',
        email: values?.email || '',
        telephone: values?.telephone || '',
      };

      if (contactData.id && contactData.id.trim() !== '') {
        await dispatch(updateLiteracyScreeningContact(contactData)).unwrap();
        await dispatch(
          fetchLiteracyScreeningContact({ showLoading: false })
        ).unwrap();
        dispatch(
          createNotification({
            severity: 'success',
            children: 'Literacy screening contact edited',
          })
        );
      } else {
        await dispatch(createLiteracyScreeningContact(contactData)).unwrap();
        await dispatch(
          fetchLiteracyScreeningContact({ showLoading: false })
        ).unwrap();
        dispatch(
          createNotification({
            severity: 'success',
            children: 'Literacy screening contact saved',
          })
        );
      }

      setIsFormDirty(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save contact:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeOfSchoolYear = async (option: any): Promise<void> => {
    if (!option) {
      return;
    }
    setSelectedSchoolYearOption(option);
  };

  const handleEditViewClick = (): void => {
    navigate(
      appRoutePaths.LiteracyScreeningInput(selectedSchoolYearOption?.value || '')
    );
  };

  const getToolTip = (dirty: boolean, isValid: boolean): string => {
    if (!dirty && isValid) {
      return '**No changes have been made**';
    } else if (dirty && !isValid) {
      return '**Make sure all required fields are complete**';
    }

    return '**Save Literacy Screening LEA Contact Information Changes**';
  };

  const getCancelToolTip = (dirty: boolean, isValid: boolean): string => {
    if (!dirty && isValid) {
      return 'No changes to cancel';
    }

    return 'Cancel Changes';
  };

  const handleCancelClick = (): void => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = (): void => {
    if (resetFormFunction) {
      resetFormFunction();
    }
    setShowCancelModal(false);
  };

  const handleCancelCancel = (): void => {
    setShowCancelModal(false);
  };

  return (
    <DistrictSchoolInfoPage
      pageClass="data-input--district-school-info--literacy-screening--list"
      isLoading={isInitialLoad || isLoadingContactList || isLoadingSchoolYears}
      loadingDataId={'literacy-screening-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div className="subheading-and-button-container">
            <h4 data-testid={'component-heading-text'}>Literacy Screening</h4>
            <span style={{ color: 'red' }}>* Required Field</span>
          </div>
          <div>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={LiteracyScreeningListPageValidationSchema}
              //eslint-disable-next-line @typescript-eslint/no-empty-function
              onSubmit={() => { }}
              enableReinitialize={true}
              validateOnChange={true}
              validateOnMount={true}
            >
              {({ dirty, isValid, resetForm, values }) => {
                if (dirty !== isFormDirty) {
                  setIsFormDirty(dirty);
                }

                if (resetForm !== resetFormFunction) {
                  setResetFormFunction(() => resetForm);
                }

                return (
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      return false;
                    }}
                  >
                    <Panel
                      panelType={PanelType.INFO}
                      heading="Literacy Screening LEA Contact Information"
                    >
                      <div className="contacts-container">
                        <div>
                          <div className="field-row">
                            <FormTextbox
                              displayName="First Name"
                              field="firstName"
                              disabled={isContactReadOnly}
                              validationSchema={
                                LiteracyScreeningListPageValidationSchema
                              }
                            />
                            <FormTextbox
                              displayName="Last Name"
                              field="lastOrSurname"
                              disabled={isContactReadOnly}
                              validationSchema={
                                LiteracyScreeningListPageValidationSchema
                              }
                            />
                            <FormTextbox
                              displayName="Position"
                              field="position"
                              disabled={isContactReadOnly}
                              validationSchema={
                                LiteracyScreeningListPageValidationSchema
                              }
                            />
                          </div>
                          <br />
                          <div className="field-row">
                            <FormTextbox
                              displayName="Email"
                              field="email"
                              disabled={isContactReadOnly}
                              validationSchema={
                                LiteracyScreeningListPageValidationSchema
                              }
                            />
                            <FormTextbox
                              displayName="Telephone Number"
                              field="telephone"
                              disabled={isContactReadOnly}
                              validationSchema={
                                LiteracyScreeningListPageValidationSchema
                              }
                            />
                          </div>
                        </div>
                        <div className="form-actions">
                          <ActionButton
                            classes="button"
                            size={ButtonSize.SMALL}
                            onClick={handleCancelClick}
                            dataTestId="cancel-button"
                            tooltipText={getCancelToolTip(dirty, isValid)}
                            disabled={isContactReadOnly || !dirty || isSaving}
                          >
                            <span>Cancel</span>
                          </ActionButton>
                          <ActionButton
                            classes="button--secondary"
                            size={ButtonSize.SMALL}
                            buttonType="button"
                            dataTestId="submit-button"
                            tooltipText={getToolTip(dirty, isValid)}
                            disabled={
                              isContactReadOnly || !isValid || !dirty || isSaving
                            }
                            loading={isSaving}
                            onClick={async (
                              e: React.MouseEvent<HTMLButtonElement>
                            ) => {
                              e.preventDefault();
                              e.stopPropagation();
                              await handleSubmit(values, resetForm);
                              return false;
                            }}
                          >
                            <span>Save</span>
                          </ActionButton>
                        </div>
                      </div>
                    </Panel>
                  </Form>
                );
              }}
            </Formik>
          </div>
          <br />
          <div className="filters-selection-container">
            <div className="dropdown-selection">
              <StyledSingleDropdown
                fieldName="schoolYear"
                options={schoolYearOptions}
                onChange={handleOnChangeOfSchoolYear}
                value={selectedSchoolYearOption}
                isSearchable={false}
                isClearable={false}
                labelText="School Year"
                showLabel={true}
                disabled={disableSchoolYearActions}
                tooltipText={
                  disableSchoolYearActions
                    ? isSaving
                      ? 'Cannot change school year while saving contact'
                      : 'Cannot change school year while form has unsaved changes'
                    : 'Select school year'
                }
              />
            </div>
            <ActionButton
              onClick={handleEditViewClick}
              classes="no-wrap-text edit-view-button"
              dataTestId="edit-view-button"
              tooltipText={
                !isContactAvailable
                  ? 'Save Literacy Screening LEA Contact Information'
                  : disableSchoolYearActions
                    ? isSaving
                      ? 'Cannot edit while saving contact'
                      : 'Cannot edit while form has unsaved changes'
                    : 'Edit/View Literacy Screening'
              }
              disabled={disableEditView}
            >
              <>Edit/View</>
            </ActionButton>
            <div
              className="edit-view-helper-text"
              data-testid="edit-view-helper-text"
            >
              Click the Edit/View button to access the Literacy Screening
              Information.
            </div>
          </div>
        </div>
      </div>

      {showCancelModal && (
        <Modal
          type={DialogType.CONFIRM}
          open={showCancelModal}
          onClose={handleCancelCancel}
          title={'Are you sure?'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              Your changes have not been saved. Do you wish to continue?
            </p>
            <div className="button-row">
              <ActionButton
                onClick={handleCancelConfirm}
                dataTestId="yes-button"
                tooltipText={'Navigate to the last page'}
              >
                <span>Yes</span>
              </ActionButton>
              <ActionButton
                classes="button--secondary"
                onClick={handleCancelCancel}
                dataTestId="no-button"
                tooltipText={'Stay on existing page'}
              >
                <span>No</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </DistrictSchoolInfoPage>
  );
};

export default LiteracyScreeningList;
